import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Paper, Typography, Button, Stack } from '@mui/material';
import { useTopBar } from '../TopBarContext';
import VerticalAZCatalog from './VerticalAZCatalog';
import PersonDossier from './PersonDossier';
import EmployeeList from '../EmployeeList';
import { peopleService } from '../../services/api';
import { usePermissions } from '../PermissionsContext';      // S3
import NewHireWizard from '../Employment/NewHireWizard';     // S3

export default function EmployeeCaseVerticalPage(){
  const location = useLocation();
  const [letter, setLetter] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [initialLoadDone, setInitialLoadDone] = React.useState(false);
  // S3 — new hire wizard (person + employee + contract in one step)
  const { hasPermission } = usePermissions();
  const canHire = hasPermission('Employee.Manage') && hasPermission('ManageContracts');
  const [hireOpen, setHireOpen] = React.useState(false);
  const [listKey, setListKey] = React.useState(0);

  const { setTopBarContent, clearTopBarContent } = useTopBar();
  React.useEffect(()=>()=>{ clearTopBarContent(); },[clearTopBarContent]);

  const handleSelect = (p) => {
    const sel = p ? { Id: p.Id ?? p.id, FullName: p.FullName || p.fullName, ...p } : null;
    setSelected(sel);
    if (sel) {
      setTopBarContent({ center: <Typography variant="h6" sx={{ fontWeight:500, whiteSpace:'nowrap' }}>{sel.FullName || sel.fullName}</Typography> });
    } else {
      clearTopBarContent();
    }
  };

  // Handle navigation state from HR Dashboard (selectPersonId)
  React.useEffect(() => {
    if (initialLoadDone) return;
    const selectPersonId = location.state?.selectPersonId;
    if (selectPersonId) {
      (async () => {
        try {
          const person = await peopleService.getById(selectPersonId);
          if (person) {
            handleSelect(person);
          }
        } catch (err) {
          console.warn('Failed to load person from navigation state:', err);
        }
        setInitialLoadDone(true);
      })();
    } else {
      setInitialLoadDone(true);
    }
  }, [location.state, initialLoadDone]);

  // Handle employee row click from EmployeeList
  const handleEmployeeRowClick = (employee) => {
    // Get person ID from multiple possible sources
    const pid = employee.personId || employee.person?.id || employee.person?.Id;
    if (!pid) {
      console.warn('No personId found for employee:', employee);
      return;
    }
    // Map employee to person format
    handleSelect({
      Id: pid,
      id: pid,
      FullName: `${employee.person?.firstName || ''} ${employee.person?.lastName || ''}`.trim() || employee.email,
      fullName: `${employee.person?.firstName || ''} ${employee.person?.lastName || ''}`.trim() || employee.email,
      ...employee.person
    });
  };

  // Load summary once per selection and pass to children
  React.useEffect(()=>{
    let active = true;
    (async ()=>{
      if(!selected?.Id) { setSummary(null); return; }
      try { const s = await peopleService.getProfileSummary(selected.Id); if(active) setSummary(s); }
      catch { if(active) setSummary(null); }
    })();
    return ()=>{ active = false };
  }, [selected?.Id]);

  return (
    <Box sx={{ p: 2, display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: '44px 1fr',
        md: '44px 280px 1fr',
        lg: '44px 360px 1fr'
      },
      gap: 1 }}>
      {/* AZ + Catalog - always visible */}
      <Box sx={{ gridColumn: { xs:'1', md:'1 / span 2' }, position:'sticky', alignSelf:'start', top:64, zIndex:1 }}>
        <VerticalAZCatalog letter={letter} onLetterChange={setLetter} selectedId={selected?.Id} onSelect={handleSelect} collapsed={false} />
      </Box>

      {/* Right panel - EmployeeList or PersonDossier */}
      <Paper elevation={0} sx={{ p: 1.5, minHeight: 400, overflow:'hidden', boxShadow:'none', border:'none', bgcolor:'transparent' }}>
        {selected ? (
          <PersonDossier person={{ ...selected }} summary={summary} onBack={() => handleSelect(null)} />
        ) : (
          <>
            {canHire && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Button variant="contained" onClick={() => setHireOpen(true)}>New hire</Button>
              </Stack>
            )}
            <EmployeeList
              key={listKey}
              embedded={true}
              hideActions={true}
              onRowClick={handleEmployeeRowClick}
            />
          </>
        )}
      </Paper>
      <NewHireWizard open={hireOpen} onClose={() => setHireOpen(false)}
        onCreated={(r, name) => {
          setHireOpen(false);
          setListKey((k) => k + 1);
          handleSelect({ Id: r.personId, id: r.personId, FullName: name, fullName: name });
        }} />
    </Box>
  );
}
